import React, { useEffect, useState } from 'react';
import { Tag, Plus, Trash2, RefreshCw, X, Check, Ban } from 'lucide-react';
import { api } from '@/lib/api';
import { TextField, Toggle, Select, Drawer } from '@/components/admin/shared';

const KIND_OPTS = [
  { value: 'percent', label: 'Percent (%)' },
  { value: 'fixed',   label: 'Fixed amount' },
];

function fmtDate(s) {
  if (!s) return '';
  try { return new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return ''; }
}

function defaultForm() {
  return { code: '', kind: 'percent', value: 10, active: true, max_uses: '', min_subtotal: 0, expires_at: '', applies_to_product_ids: [] };
}

export default function DiscountCodesManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm());
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/discount-codes');
      setItems(data || []);
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Failed to load discount codes');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing(null); setForm(defaultForm()); setOpen(true); };
  const startEdit = (it) => {
    setEditing(it);
    setForm({
      code: it.code,
      kind: it.kind,
      value: it.value,
      active: it.active,
      max_uses: it.max_uses ?? '',
      min_subtotal: it.min_subtotal ?? 0,
      expires_at: it.expires_at || '',
      applies_to_product_ids: it.applies_to_product_ids || [],
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        code: (form.code || '').trim().toUpperCase(),
        kind: form.kind,
        value: Number(form.value) || 0,
        active: !!form.active,
        min_subtotal: Number(form.min_subtotal) || 0,
      };
      if (form.max_uses !== '' && form.max_uses !== null && form.max_uses !== undefined) {
        payload.max_uses = Number(form.max_uses);
      }
      if (form.expires_at) payload.expires_at = form.expires_at;
      if (editing) {
        await api.put(`/admin/discount-codes/${editing.id}`, payload);
        setMsg('Discount code updated.');
      } else {
        await api.post('/admin/discount-codes', payload);
        setMsg('Discount code created.');
      }
      setOpen(false);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!confirm(`Delete code ${it.code}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/discount-codes/${it.id}`);
      setMsg('Discount code deleted.');
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="discount-codes-manager">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Tag size={18} className="text-[var(--pg-gold-2)]" />
          <h3 className="font-serif text-lg text-[var(--pg-ink)]">Discount Codes</h3>
          <span className="text-[10px] font-mono tracking-[0.24em] uppercase text-[var(--pg-ink-faint)]">{items.length} total</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="pg-btn-ghost px-3 py-2 rounded-[var(--pg-radius-sm)] text-xs flex items-center gap-2">
            <RefreshCw size={14} /> Refresh
          </button>
          <button data-testid="discount-code-new" onClick={startNew} className="pg-btn-primary px-3 py-2 rounded-[var(--pg-radius-sm)] text-xs flex items-center gap-2">
            <Plus size={14} /> New code
          </button>
        </div>
      </div>

      {msg && (
        <div className="pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-xs text-[var(--pg-ink)] flex items-center justify-between">
          <span className="flex items-center gap-2"><Check size={14} className="text-[var(--pg-emerald-2)]" /> {msg}</span>
          <button onClick={() => setMsg(null)}><X size={14} className="text-[var(--pg-ink-faint)]" /></button>
        </div>
      )}

      <div className="pg-admin-card rounded-[var(--pg-radius-md)] overflow-hidden">
        {loading ? (
          <div className="p-6 text-xs font-mono text-[var(--pg-ink-faint)]">Loading discount codes…</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-xs font-mono text-[var(--pg-ink-faint)]">No discount codes yet. Create one to start.</div>
        ) : (
          <div className="divide-y divide-[var(--pg-border-2)]">
            {items.map((it) => (
              <div key={it.id} className="p-4 flex items-center gap-4" data-testid={`discount-row-${it.code}`}>
                <button onClick={() => startEdit(it)} className="flex-1 min-w-0 text-left">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-serif text-base text-[var(--pg-ink)]">{it.code}</span>
                    <span
                      className="px-2 py-0.5 rounded-full text-[10px] font-mono tracking-[0.2em] uppercase"
                      style={{
                        background: it.active ? 'rgba(25,194,142,0.14)' : 'rgba(255,255,255,0.05)',
                        border: `1px solid ${it.active ? 'rgba(25,194,142,0.5)' : 'rgba(255,255,255,0.2)'}`,
                        color: it.active ? 'var(--pg-emerald-2)' : 'var(--pg-ink-muted)',
                      }}
                    >
                      {it.active ? 'active' : 'disabled'}
                    </span>
                  </div>
                  <p className="text-[11px] font-mono text-[var(--pg-ink-muted)] mt-1">
                    {it.kind === 'percent' ? `${it.value}% off` : `$${Number(it.value).toFixed(2)} off`}
                    {it.min_subtotal ? ` · min $${Number(it.min_subtotal).toFixed(2)}` : ''}
                    {it.max_uses ? ` · ${it.uses_count || 0}/${it.max_uses} used` : ` · ${it.uses_count || 0} used`}
                    {it.expires_at ? ` · expires ${fmtDate(it.expires_at)}` : ''}
                  </p>
                </button>
                <button data-testid={`discount-delete-${it.code}`} onClick={() => remove(it)} className="p-2 text-[var(--pg-ink-muted)] hover:text-[var(--pg-neon-pink)]" aria-label="delete">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? `Edit ${editing.code}` : 'New Discount Code'}
        onSave={save}
        saving={saving}
      >
        <TextField label="Code (uppercased on save)" value={form.code} onChange={(v) => setForm({ ...form, code: v })} placeholder="WELCOME10" />
        <div className="grid grid-cols-2 gap-3">
          <Select label="Kind" value={form.kind} onChange={(v) => setForm({ ...form, kind: v })} options={KIND_OPTS} />
          <TextField label={form.kind === 'percent' ? 'Percent (1-100)' : 'Amount'} type="number" value={form.value} onChange={(v) => setForm({ ...form, value: v })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Minimum subtotal" type="number" value={form.min_subtotal} onChange={(v) => setForm({ ...form, min_subtotal: v })} />
          <TextField label="Max uses (blank = unlimited)" type="number" value={form.max_uses} onChange={(v) => setForm({ ...form, max_uses: v })} />
        </div>
        <TextField label="Expires (ISO date, optional)" value={form.expires_at} onChange={(v) => setForm({ ...form, expires_at: v })} placeholder="2026-12-31T00:00:00Z" />
        <Toggle label="Active" value={form.active} onChange={(v) => setForm({ ...form, active: v })} />
      </Drawer>
    </div>
  );
}
